import React from 'react';
import {
  PackageFormValues,
  PackageVisibility,
  PACKAGE_IMAGE_PLACEHOLDER,
} from '@/types/package';
import { formatRupiah, sanitizeHtml } from '@/utils';

interface PackagePreviewCardProps {
  values: PackageFormValues;
}

const DAYS_PER_MONTH = 30;

/** Months added to the instalment count for the tenor upper bound. */
// TODO: confirm the real tenor rule with product — the design shows
// "30 - 36 Bulan" against a 30x instalment, so the upper bound is inferred.
const TENOR_SPAN_MONTHS = 6;

const toNumber = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const PreviewRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <span className="text-[14px] text-gray-500">{label}</span>
    <span className="text-[14px] text-right">{children}</span>
  </div>
);

export const PackagePreviewCard: React.FC<PackagePreviewCardProps> = ({
  values,
}) => {
  const dailyPrice = toNumber(values.dailyPrice);
  const freeDays = values.freeDaysEnabled
    ? values.freeDayDates.filter((entry) => entry.day.trim())
    : [];
  const freeDayCount = freeDays.length;

  // Free days are not charged, so a month bills for the remainder.
  const billableDays = Math.max(DAYS_PER_MONTH - freeDayCount, 0);
  const monthlyCost = dailyPrice * billableDays;

  const installments = toNumber(values.maxInstallments);
  const minDp = values.downPaymentEnabled ? toNumber(values.minDp) : 0;
  const deposit = values.depositEnabled ? toNumber(values.deposit) : 0;
  const remainingDeposit = Math.max(deposit - minDp, 0);
  // The API stores the NON-refundable portion; what the driver gets back is
  // the remainder (TRD C2).
  const refundableDeposit = Math.max(
    deposit - toNumber(values.depositNonRefundable),
    0
  );

  // Artwork is whatever was uploaded for this package; the placeholder stands
  // in until then, dimmed so an unfinished form still reads as unfinished.
  const packageName = values.name.trim();
  const hasName = !!packageName;
  const artwork = values.imageURL || '';

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-col items-center px-6 pt-6 pb-4 border-b border-gray-200">
        {values.visibility === PackageVisibility.PRIVATE && (
          <span className="mb-3 self-start inline-flex items-center gap-1 rounded-md bg-[#111827E6] px-2 py-1 text-[12px] font-medium text-white">
            <i className="ri-eye-off-line text-[14px]"></i>
            Khusus · tidak tampil di katalog
          </span>
        )}
        <img
          src={artwork || PACKAGE_IMAGE_PLACEHOLDER}
          alt={
            artwork
              ? `${packageName || 'Package'} artwork`
              : 'Package artwork preview'
          }
          // 2:1 to match the recommended 1024x512 upload, so what the form
          // previews is the shape the uploader asked for.
          className={`w-full aspect-[2/1] rounded-lg object-cover bg-gray-100 ${
            artwork ? '' : 'opacity-40 object-contain'
          }`}
        />
        <h3
          className={`mt-2 text-[32px] leading-[40px] ${
            hasName ? 'text-gray-900' : 'text-gray-300'
          }`}
        >
          {hasName ? packageName : '-'}
        </h3>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center rounded-md bg-[#EFEAF7] px-2 py-1 text-[13px] font-medium text-primary">
            TIER {hasName ? packageName : '-'}
          </span>
          <span className="text-[22px] font-semibold text-gray-900">
            {formatRupiah(dailyPrice, 'Rp0', { space: false })} / hari
          </span>
        </div>

        {values.description.trim() && (
          <p className="mt-3 text-[14px] leading-[22px] text-gray-500">
            {values.description}
          </p>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-200">
        <h4 className="text-[16px] font-semibold text-gray-900 mb-2">
          Estimasi Biaya &amp; Sewa
        </h4>
        <PreviewRow label="Biaya Sewa / bulan">
          <span className="font-medium text-gray-900">
            {formatRupiah(monthlyCost, 'Rp0', { space: false })}
          </span>
        </PreviewRow>
        <PreviewRow label="Hari Gratis">
          {freeDayCount > 0 ? (
            <span className="font-medium text-emerald-500">
              {freeDayCount} Hari / bulan (tgl{' '}
              {freeDays.map((entry) => entry.day).join(' & ')})
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </PreviewRow>
        <PreviewRow label="Jangka Waktu (Tenor)">
          {installments > 0 ? (
            <span className="font-semibold text-gray-900">
              {installments} - {installments + TENOR_SPAN_MONTHS} Bulan
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </PreviewRow>
      </div>

      {values.depositEnabled && (
        <div className="px-6 py-4 border-t border-gray-200">
          <h4 className="text-[16px] font-semibold text-gray-900 mb-2">
            Informasi Deposit
          </h4>
          <PreviewRow label="Uang Muka (DP)">
            <span className="font-medium text-gray-900">
              {formatRupiah(minDp, 'Rp0', { space: false })}
            </span>
          </PreviewRow>
          <PreviewRow
            label={`Sisa Deposit${
              installments > 0 ? ` (Dicicil ${installments}x)` : ''
            }`}
          >
            <span className="font-medium text-gray-900">
              {formatRupiah(remainingDeposit, 'Rp0', { space: false })}
            </span>
          </PreviewRow>

          {refundableDeposit > 0 && (
            <p className="mt-3 text-[13px] leading-[20px] text-gray-500">
              * Deposit sebesar{' '}
              <span className="font-semibold text-gray-900">
                {formatRupiah(refundableDeposit, 'Rp0', { space: false })}
              </span>{' '}
              bersifat refundable dan akan dikembalikan utuh setelah masa sewa
              berakhir.
            </p>
          )}
        </div>
      )}

      {values.benefit.trim() && (
        <div className="px-6 py-4 border-t border-gray-200">
          <h4 className="text-[16px] font-semibold text-gray-900 mb-2">
            Benefit
          </h4>
          <div
            className="text-[14px] leading-[22px] text-gray-600 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(values.benefit) }}
          />
        </div>
      )}

      {values.termsAndConditions.trim() && (
        <div className="px-6 py-4 border-t border-gray-200">
          <h4 className="text-[16px] font-semibold text-gray-900 mb-2">
            Syarat &amp; Ketentuan
          </h4>
          <div
            className="text-[14px] leading-[22px] text-gray-600 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_li]:marker:text-primary [&_li]:marker:font-medium"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(values.termsAndConditions),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PackagePreviewCard;
